import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  selectSessionId,
  selectConversationSessionSpec,
  selectAPISession,
  setStreamingData,
  selectPreferredCodecOverride,
  selectVideoCodec,
} from "../../store/sessionSlice";
import { selectUser, selectTempProfilePictureURL } from "../../store/userSlice";
import { selectDevMode } from '../../store/flagsSlice';
import {
  selectGUIThemeOverride,
  setGUITheme,
} from "../../store/userSettingsSlice";
import { getGUIThemeProperties, getAvailableGUIThemes, isValidGUITheme } from "../../utils/baseConfig";
import config from "../../utils/config";
import { endSession } from "../../utils/sessionEndpoints";
import { createStringConsoleMessage } from "../../utils/protobuf_utils";
import { WebSocketConnection, ConnectionStatus } from "../../utils/WebSocketConnection";

import { useNavUtils } from "../../utils/navUtils";
import AudioControls from "../AudioControls";
import { DisplayMode } from "../../utils/baseConfig";
import { AudioMode } from "../../utils/audioUtils";
import { 
  SupportedCodecKey,
  useCodecCapabilitiesWithHardware,
} from "../../hooks/useDecoderCapabilities";

import aspectFitIcon from "../../assets/aspect_fit.svg";
import aspectFillIcon from "../../assets/aspect_fill.svg";
import logoutIcon from "../../assets/logout.svg";
import privacyPolicyIcon from "../../assets/privacy_policy.svg";
import tocIcon from "../../assets/toc.svg";
import endSessionIcon from "../../assets/battle_axe.svg";
import startSessionIcon from "../../assets/start_icon.svg";
import resetSessionIcon from "../../assets/reset_icon.svg";
import feedbackIcon from "../../assets/feedback.svg";
import maximizeIcon from "../../assets/maximize.svg";
import "../../styles/components/sidepanel/Settings.css";

interface Props {
  onAudioSpeakerChange: (deviceId: string) => void;
  onAudioSpeakerMuteChange: (muted: boolean) => void;
  audioSpeakerMuteGetter: () => boolean;
  onAudioSpeakerVolumeChange: (volume: number) => void;
  audioSpeakerVolumeGetter: () => number;
  setDisplayMode?: (mode: DisplayMode) => void;
  displayMode: DisplayMode;
  setAudioMode: (mode: AudioMode) => void;
  audioMode: AudioMode;
  webSocketStatus: ConnectionStatus;
  handleFeedbackClick: () => void;
}

const Settings: React.FC<Props> = ({
  onAudioSpeakerChange,
  onAudioSpeakerMuteChange,
  audioSpeakerMuteGetter,
  onAudioSpeakerVolumeChange,
  audioSpeakerVolumeGetter,
  setDisplayMode,
  displayMode,
  setAudioMode,
  audioMode,
  webSocketStatus,
  handleFeedbackClick,
}) => {
  const { logoutReset } = useNavUtils();
  const { t } = useTranslation();

  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [textMessageInput, setTextMessageInput] = useState<string>("");

  // Get session data from Redux
  const dispatch = useDispatch();
  const sessionId = useSelector(selectSessionId);
  const sessionSpec = useSelector(selectConversationSessionSpec);
  const apiSession = useSelector(selectAPISession);
  const reduxUser = useSelector(selectUser);
  const reduxProfilePictureURL = useSelector(selectTempProfilePictureURL);
  const devMode = useSelector(selectDevMode);

  const preferredCodecOverride = useSelector(selectPreferredCodecOverride);
  const videoCodec = useSelector(selectVideoCodec);
  const guiThemeOverride = useSelector(selectGUIThemeOverride);

  // Get cached codec support
  const { codecs } = useCodecCapabilitiesWithHardware();

  const handlePreferredCodecChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const preferredCodecOverride = e.target.value === "none" ? null : e.target.value as SupportedCodecKey;
    dispatch(setStreamingData({ preferredCodecOverride }));
    console.log("[Settings] Preferred codec override set to:", preferredCodecOverride);
  };

  const handleGUIThemeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;

    // Handle default case
    if (value === "default") {
      dispatch(setGUITheme(undefined));
      console.log("[Settings] GUI style set to: default");
      return;
    }

    // Type guard ensures TypeScript knows this is a valid GUITheme
    // Dropdown is populated from getAvailableGUIThemes() so value is always valid
    if (isValidGUITheme(value)) {
      dispatch(setGUITheme(value));
      console.log("[Settings] GUI style set to:", value);

      // If the selected theme defaults to live mic, automatically switch to open-mic mode
      const themeProps = getGUIThemeProperties(value);
      if (themeProps.defaultLiveMic) {
        setAudioMode("open-mic");
        console.log("[Settings] Automatically switching to open-mic mode for theme:", value);
      }
    }
  };

  const endSessionButtonClicked = async () => {
    if (sessionId && sessionSpec) {
      try {
        console.log("[Settings] Ending session:", sessionSpec, sessionId);
        await endSession(sessionSpec, sessionId);
      } catch (error) {
        console.error("[Settings] Error ending session:", error);
      }
    } else {
      console.warn("[Settings] Cannot end session - no active session found");
    }
  };

  const logoutButtonClicked = async () => {
    await endSessionButtonClicked();
    await logoutReset();
  };

  const privacyPolicyClicked = () => {
    window.open("https://www.persona-ai.ai/privacy-policy", "_blank");
  };

  const termsOfServiceClicked = () => {
    window.open("https://www.persona-ai.ai/terms-and-conditions", "_blank");
  };

  const handleDisplayModeChange = (mode: DisplayMode) => {
    if (setDisplayMode) setDisplayMode(mode);
    console.log(`Display mode changed to: ${mode}`);
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        // Enter fullscreen
        await document.documentElement.requestFullscreen();
      } else {
        // Exit fullscreen
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error("Error toggling fullscreen:", error);
    }
  };

  const startClicked = () => {
    console.log("[SettingsView] start button clicked");
    const message = createStringConsoleMessage("/el");
    WebSocketConnection.getExistingConnectedInstance()?.sendMessage(message);
  };

  const resetClicked = () => {
    console.log("[SettingsView] reset button clicked");
    const message = createStringConsoleMessage("/reset");
    WebSocketConnection.getExistingConnectedInstance()?.sendMessage(message);
  };

  const cycleHudClicked = () => {
    console.log("[SettingsView] cycle HUD button clicked");
    const message = createStringConsoleMessage("/fc /le cyclehud");
    WebSocketConnection.getExistingConnectedInstance()?.sendMessage(message);
  };

  const handleTextMessageSubmit = () => {
    const trimmed = textMessageInput.trim();
    if (trimmed) {
      console.log("Message sent:", trimmed);
      const message = createStringConsoleMessage(trimmed);
      WebSocketConnection.getExistingConnectedInstance()?.sendMessage(message);
      setTextMessageInput("");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && textMessageInput.trim()) {
      handleTextMessageSubmit();
    }
  };

  // Listen for fullscreen changes and initialize fullscreen state
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    // Initialize fullscreen state
    setIsFullscreen(!!document.fullscreenElement);

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  return (
    <>
      {/* Profile Section */}
      <div className="settings-section settings-profile-section">
        {reduxProfilePictureURL && (
          <img
            src={reduxProfilePictureURL}
            alt={t("settings.profileAlt")}
            className="settings-profile-avatar"
          />
        )}
        <div className="settings-profile-info">
          <h1 className="settings-profile-name">
            {reduxUser?.name?.first_name || ""}
            {reduxUser?.name?.last_name ? ` ${reduxUser.name.last_name}` : ""}
          </h1>
          {reduxUser?.email && (
            <p className="settings-profile-email">{reduxUser?.email}</p>
          )}
        </div>
      </div>

      <AudioControls
        onAudioSpeakerChange={onAudioSpeakerChange}
        onAudioSpeakerMuteChange={onAudioSpeakerMuteChange}
        audioSpeakerMuteGetter={audioSpeakerMuteGetter}
        onAudioSpeakerVolumeChange={onAudioSpeakerVolumeChange}
        audioSpeakerVolumeGetter={audioSpeakerVolumeGetter}
      />

      {/* Action buttons directly on the main panel */}
      {devMode && (
        <div className="settings-section">
          <h2>{t("settings.display")}</h2>
          <div className="settings-display-mode-toggle">
            <label
              className={`settings-display-mode-option ${displayMode === "fit" ? "selected" : "unselected"
                }`}
            >
              <input
                type="radio"
                name="cover"
                value="fit"
                checked={displayMode === "fit"}
                onChange={() => handleDisplayModeChange("fit")}
              />
              <img
                src={aspectFitIcon}
                alt={t("settings.aspectFit")}
                className={`settings-radio-icon ${displayMode === "fit" ? "selected" : ""}`}
              />
              <span>{t("settings.fit")}</span>
            </label>
            <label
              className={`settings-display-mode-option ${displayMode === "fill" ? "selected" : "unselected"
                }`}
            >
              <input
                type="radio"
                name="cover"
                value="fill"
                checked={displayMode === "fill"}
                onChange={() => handleDisplayModeChange("fill")}
              />
              <img
                src={aspectFillIcon}
                alt={t("settings.aspectFill")}
                className={`settings-radio-icon ${displayMode === "fill" ? "selected" : ""}`}
              />
              <span>{t("settings.fill")}</span>
            </label>
            <label
              className={`settings-display-mode-option ${displayMode === "reres" ? "selected" : "unselected"
                }`}
            >
              <input
                type="radio"
                name="cover"
                value="reres"
                checked={displayMode === "reres"}
                onChange={() => handleDisplayModeChange("reres")}
              />
              {/* <img
                src={aspectFillIcon}
                alt={t("settings.reRes")}
                className={`settings-radio-icon ${displayMode === "reres" ? "selected" : ""}`}
              /> */}
              <span>{t("settings.reRes")}</span>
            </label>
          </div>
          {document.fullscreenEnabled && (
            <button
              className="settings-action-button settings-fullscreen-button"
              onClick={toggleFullscreen}
            >
              {isFullscreen ? t("settings.exitFullScreen") : t("settings.fullScreen")}
            </button>
          )}
        </div>
      )}

      {/* Audio mode section */}
      {devMode && (
        <div className="settings-section">
          <h2>{t("settings.audioMode")}</h2>
          <div className="settings-audio-mode-toggle">
            <label
              className={`settings-audio-mode-option ${audioMode === "push-to-talk" ? "selected" : "unselected"
                }`}
            >
              <input
                type="radio"
                name="audio-mode"
                value="push-to-talk"
                checked={audioMode === "push-to-talk"}
                onChange={() => setAudioMode("push-to-talk")}
              />
              <span>{t("settings.pushToTalk")}</span>
            </label>
            <label
              className={`settings-audio-mode-option ${audioMode === "open-mic" ? "selected" : "unselected"
                }`}
            >
              <input
                type="radio"
                name="audio-mode"
                value="open-mic"
                checked={audioMode === "open-mic"}
                onChange={() => setAudioMode("open-mic")}
              />
              <span>{t("settings.openMic")}</span>
            </label>
          </div>
        </div>
      )}

      {/* Microphone Style section */}
      {devMode && (
        <div className="settings-section">
          <h2>{t("settings.microphoneStyle")}</h2>
          <div className="settings-microphone-style-toggle">
            <label
              className={`settings-microphone-style-option ${!guiThemeOverride ? "selected" : "unselected"}`}
            >
              <input
                type="radio"
                name="microphone-style"
                value="default"
                checked={!guiThemeOverride}
                onChange={() => handleGUIThemeChange({ target: { value: "default" } } as any)}
              />
              <span>{t("settings.default")} ({config.guiTheme})</span>
            </label>
            {getAvailableGUIThemes().map((style) => {
              const styleProps = getGUIThemeProperties(style);
              const label = styleProps.allowLiveMic ? `${style} (Live!)` : style;
              
              return (
                <label
                  key={style}
                  className={`settings-microphone-style-option ${guiThemeOverride === style ? "selected" : "unselected"}`}
                >
                  <input
                    type="radio"
                    name="microphone-style"
                    value={style}
                    checked={guiThemeOverride === style}
                    onChange={() => handleGUIThemeChange({ target: { value: style } } as any)}
                  />
                  <span>{label}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Codec section */}
      {/* this is a toggle, where when expanded, it allows the user to select a codec */}
      {devMode && (
        <div className="settings-section">
          {codecs.length > 0 && (
            <>
              <h2>{t("settings.preferredCodec")}</h2>
              <select
                className="settings-codec-select"
                value={preferredCodecOverride ?? "none"}
                onChange={handlePreferredCodecChange}
              >
                <option value="none">{t("settings.defaultNoOverride")}</option>
                {codecs.map((codec) => (
                  <option key={codec.codec} value={codec.codec}>
                    {codec.codec} {codec.hardware ? `(${t("settings.hardware")})` : `(${t("settings.software")})`}
                  </option>
                ))}
              </select>
            </>
          )}
          <p><b>{t("settings.currentCodec")}</b> {videoCodec ?? t("settings.unknown")}</p>
        </div>
      )}

      <div className="settings-action-buttons-container">
        <button className="settings-action-button" onClick={termsOfServiceClicked}>
          <img src={tocIcon} alt="" className="settings-action-icon" />
          {t("settings.termsAndConditions")}
        </button>
        <button className="settings-action-button" onClick={privacyPolicyClicked}>
          <img src={privacyPolicyIcon} alt="" className="settings-action-icon" />
          {t("settings.privacyPolicy")}
        </button>
        <button className="settings-action-button" onClick={handleFeedbackClick}>
          <img src={feedbackIcon} alt="" className="settings-action-icon" />
          {t("settings.feedback")}
        </button>
        <button className="settings-action-button" onClick={logoutButtonClicked}>
          <img src={logoutIcon} alt="" className="settings-action-icon" />
          {t("user.logOut")}
        </button>
      </div>

      {devMode && (
        <>
          <br />
          <div className="settings-action-buttons-container">
            <button
              className="settings-action-button"
              onClick={endSessionButtonClicked}
              disabled={!sessionId}
              style={!sessionId ? { opacity: 0.5, cursor: "not-allowed" } : {}}
            >
              <img src={endSessionIcon} alt="" className="settings-action-icon" />
              {t("settings.endSession")}
            </button>
            <button className="settings-action-button" onClick={startClicked}>
              <img src={startSessionIcon} alt="" className="settings-action-icon" />
              {t("settings.start")}
            </button>
            <button className="settings-action-button" onClick={resetClicked}>
              <img src={resetSessionIcon} alt="" className="settings-action-icon" />
              {t("settings.reset")}
            </button>
            <button className="settings-action-button" onClick={cycleHudClicked}>
              <img src={maximizeIcon} alt="" className="settings-action-icon" />
              {t("settings.cycleHud")}
            </button>
            <div className="settings-text-input-container">
              <input
                type="text"
                className="settings-text-input"
                placeholder={t("settings.typeMessage")}
                value={textMessageInput}
                onChange={(e) => setTextMessageInput(e.target.value)}
                onKeyDown={handleKeyPress}
              />
              <button className="settings-submit-text-button" onClick={handleTextMessageSubmit}>
                {t("settings.submit")}
              </button>
            </div>
          </div>
          <br />
          <div className="settings-dev-container ">
            <span className="settings-dev">
              <b>{t("settings.userId")}</b> {apiSession?.internal_user_id}
            </span>
            <span className="settings-dev">
              <b>{t("settings.organizationId")}</b> {apiSession?.organization_id}
            </span>
            <span className="settings-dev">
              <b>{t("settings.connectionEnv")}</b> {apiSession?.connection_method}
            </span>
            <span className="settings-dev">
              <b>{t("settings.buildId")}</b> {apiSession?.build_id}
            </span>
            <span className="settings-dev">
              <b>{t("settings.sessionId")}</b> {apiSession?.session_id}
            </span>
            <span className="settings-dev">
              <b>{t("settings.conversationId")}</b> {apiSession?.conversation_id}
            </span>
            <span className="settings-dev">
              <b>{t("settings.webSocket")}</b> {webSocketStatus}
            </span>
            <span className="settings-dev">
              <b>{t("settings.supportedCodecs")}</b> {codecs ? codecs.map(codec => `${codec.codec} ${codec.hardware ? `(${t("settings.hardware")})` : `(${t("settings.software")})`}`).join(", ") : t("settings.loading")}
            </span>
          </div>
        </>
      )}
    </>
  );
};

export default Settings;
